import {
  Content,
  ContentWorkingCopy,
  WorkingCopyKey,
  InlineComment,
  CollectionWorkingCopy,
  Collection,
} from '@amzn/awscat-pattern-library-service-model';
import { DateTime } from 'luxon';
import { localLog } from '../loggers/Logger';
import { ExpandableSectionState } from '../model/Enum';
import GraphSettingOptions from '../model/GraphSettingOptions';
import TableSettingOptions from '../model/TableSettingOptions';
import { ContentChecklistState } from './ContentChecklistState';
import JSONSerializer from './JSONSerializer';
import { LocalStorageData } from './LocalStorageData';
import { ChecklistState } from '../model/ChecklistState';
import { ContentQualityQuestionnaireState } from './ContentQualityQuestionnaireState';

const serializer = new JSONSerializer();
// Storage access can throw (e.g. quota exceeded or storage disabled by the browser); treat a
// failed read as a cache miss and a failed write as "not persisted" rather than crashing callers.
function readItem(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch (err) {
    localLog.warn({ err, key }, 'Local storage read failed.');
    return null;
  }
}

function writeItem(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    localLog.warn({ err, key }, 'Local storage write failed.');
    removeItem(key);
  }
}

function removeItem(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    localLog.warn({ err, key }, 'Local storage remove failed.');
  }
}

class LocalStorage {
  public static getAuthState() {
    return readItem(LocalStorage.STATE_VALUE);
  }

  // @ts-expect-error TS7006 - strict migration
  public static setAuthState(value) {
    writeItem(LocalStorage.STATE_VALUE, value);
  }

  public static removeAuthState() {
    removeItem(LocalStorage.STATE_VALUE);
  }

  public static setContentChecklistState(
    contentId: string,
    contentChecklistState: ContentChecklistState,
  ) {
    const str = serializer.stringify(ContentChecklistState.create(contentChecklistState));
    writeItem(LocalStorage.getContentChecklistStateKey(contentId), str);
  }

  public static resetContentChecklistState(contentId: string) {
    removeItem(LocalStorage.getContentChecklistStateKey(contentId));
  }

  public static getContentChecklistState(contentId: string): ContentChecklistState {
    const localStorageValue = readItem(LocalStorage.getContentChecklistStateKey(contentId));
    let checklist: ContentChecklistState;
    if (localStorageValue) {
      try {
        checklist = serializer.deserialize<ContentChecklistState>(
          localStorageValue,
          ContentChecklistState,
        );
      } catch (err) {
        localLog.warn({ err }, 'Error while deserizalizing checklist status.');
        LocalStorage.resetContentChecklistState(contentId);
      }
    }
    // @ts-expect-error TS2454 - strict migration
    return checklist;
  }

  public static setContentQualityQuestionnaireState(
    contentId: string,
    workflowId: number,
    questionnaireState: ContentQualityQuestionnaireState,
  ) {
    const str = serializer.stringify(ContentQualityQuestionnaireState.create(questionnaireState));
    writeItem(LocalStorage.getContentQualityQuestionnaireStateKey(contentId, workflowId), str);
  }

  public static resetContentQualityQuestionnaireState(contentId: string, workflowId: number) {
    removeItem(LocalStorage.getContentQualityQuestionnaireStateKey(contentId, workflowId));
  }

  public static getContentQualityQuestionnaireState(contentId: string, workflowId: number) {
    const localStorageValue = readItem(
      LocalStorage.getContentQualityQuestionnaireStateKey(contentId, workflowId),
    );
    let questionnaireState: ContentQualityQuestionnaireState;
    if (localStorageValue) {
      try {
        questionnaireState = serializer.deserialize<ContentQualityQuestionnaireState>(
          localStorageValue,
          ContentQualityQuestionnaireState,
        );
      } catch (err) {
        localLog.warn({ err }, 'Error while deserializing questionnaire status.');
        LocalStorage.resetContentQualityQuestionnaireState(contentId, workflowId);
      }
    }
    // @ts-expect-error TS2454 - strict migration
    return questionnaireState;
  }

  public static setContentWorkingCopy(
    contentId: string,
    workingCopyKey: WorkingCopyKey,
    workingCopy: ContentWorkingCopy,
    creationDate: DateTime,
  ) {
    const str = serializer.stringify(workingCopy);
    const data = serializer.stringify(LocalStorageData.create(creationDate.toMillis(), str));
    writeItem(LocalStorage.getContentWorkingCopyKey(workingCopyKey, contentId), data);
  }

  public static removeContentWorkingCopy(contentId: string, workingCopyKey: string) {
    removeItem(LocalStorage.getContentWorkingCopyKey(workingCopyKey, contentId));
  }

  // @ts-expect-error TS7030 - strict migration
  public static getContentWorkingCopy(contentId: string, workingCopyKey: string) {
    const localStorageValue = readItem(
      LocalStorage.getContentWorkingCopyKey(workingCopyKey, contentId),
    );
    if (localStorageValue) {
      try {
        const lsData = serializer.deserialize(localStorageValue, LocalStorageData);
        let workingCopy = serializer.deserialize<ContentWorkingCopy>(
          lsData.data,
          ContentWorkingCopy,
        );
        if (!workingCopy.content) {
          // for backwards compatibility
          const content: Content = serializer.deserialize<Content>(localStorageValue, Content);
          workingCopy = { content };
        }
        return { createdAt: DateTime.fromMillis(lsData.timestamp), workingCopy };
      } catch (err) {
        localLog.warn({ err }, 'Error while deserizalizing content.');
        LocalStorage.removeContentWorkingCopy(contentId, workingCopyKey);
      }
    }
  }

  // @ts-expect-error TS7030 - strict migration
  public static getCollectionWorkingCopy(collectionId: string, workingCopyKey: string) {
    const localStorageValue = readItem(
      LocalStorage.getCollectionWorkingCopyKey(workingCopyKey, collectionId),
    );
    if (localStorageValue) {
      try {
        const lsData = serializer.deserialize(localStorageValue, LocalStorageData);
        let workingCopy = serializer.deserialize<CollectionWorkingCopy>(
          lsData.data,
          CollectionWorkingCopy,
        );
        if (!workingCopy.collection) {
          // for backwards compatibility
          const collection: Collection = serializer.deserialize<Collection>(
            localStorageValue,
            Collection,
          );
          workingCopy = { collection };
        }
        return { createdAt: DateTime.fromMillis(lsData.timestamp), workingCopy };
      } catch (e) {
        localLog.warn({ e }, 'Error while deserizalizing collection.');
        LocalStorage.removeCollectionWorkingCopy(collectionId, workingCopyKey);
      }
    }
  }

  public static removeCollectionWorkingCopy(collectionId: string, workingCopyKey: string) {
    removeItem(LocalStorage.getCollectionWorkingCopyKey(workingCopyKey, collectionId));
  }

  public static setCollectionWorkingCopy(
    collectionId: string,
    workingCopyKey: WorkingCopyKey,
    workingCopy: CollectionWorkingCopy,
    creationDate: DateTime,
  ) {
    const str = serializer.stringify(workingCopy);
    const data = serializer.stringify(LocalStorageData.create(creationDate.toMillis(), str));
    writeItem(LocalStorage.getCollectionWorkingCopyKey(workingCopyKey, collectionId), data);
  }

  public static setTableSettingOptions(tableId: string, tableSettingOptions: TableSettingOptions) {
    const str = serializer.stringify(tableSettingOptions);
    writeItem(LocalStorage.getTableSettingOptionsKey(tableId), str);
  }

  public static getTableSettingOptions(tableId: string): TableSettingOptions {
    const str = readItem(LocalStorage.getTableSettingOptionsKey(tableId));
    let tableSetting = TableSettingOptions.create({});
    if (str) {
      try {
        tableSetting = serializer.deserialize<TableSettingOptions>(str, TableSettingOptions);
      } catch (err) {
        localLog.warn({ err }, 'Error while deserizalizing table setting options cache data.');
        this.removeTableSettingOptions(tableId);
      }
    }
    return tableSetting;
  }

  public static removeTableSettingOptions(tableId: string) {
    removeItem(LocalStorage.getTableSettingOptionsKey(tableId));
  }

  public static setTabId(tabId: string, activeTabId: string) {
    writeItem(LocalStorage.getTabSettingOptionsKey(tabId), activeTabId);
  }

  public static getCachedTabId(tabId: string): string | null {
    return readItem(LocalStorage.getTabSettingOptionsKey(tabId));
  }

  public static setGraphSettingOptions(graphId: string, graphSettingOptions: GraphSettingOptions) {
    const graphSettingStr = serializer.stringify(graphSettingOptions);
    writeItem(LocalStorage.getGraphSettingKey(graphId), graphSettingStr);
  }

  public static getGraphSettingOptions(graphId: string): GraphSettingOptions {
    const graphSettingStr = readItem(LocalStorage.getGraphSettingKey(graphId));
    let graphSetting = GraphSettingOptions.create({});
    if (graphSettingStr) {
      try {
        graphSetting = serializer.deserialize<GraphSettingOptions>(
          graphSettingStr,
          GraphSettingOptions,
        );
      } catch (err) {
        localLog.warn({ err }, 'Error while deserizalizing graph setting options cache data.');
        this.removeGraphSettingOptions(graphId);
      }
    }
    return graphSetting;
  }

  public static removeGraphSettingOptions(graphId: string): void {
    removeItem(LocalStorage.getGraphSettingKey(graphId));
  }

  public static setExpandableSectionState(
    expandableSectionId: string,
    expandedSectionState: ExpandableSectionState,
  ): void {
    writeItem(LocalStorage.getExpandableSectionStateKey(expandableSectionId), expandedSectionState);
  }

  public static getExpandableSectionState(expandableSectionId: string): ExpandableSectionState {
    return readItem(
      LocalStorage.getExpandableSectionStateKey(expandableSectionId),
    ) as ExpandableSectionState;
  }

  public static setChecklistState(
    contentId: string,
    checklistState: ChecklistState,
    checklistKey: string,
  ) {
    const str = serializer.stringify(ChecklistState.create(checklistState));
    writeItem(LocalStorage.getChecklistStateKey(checklistKey, contentId), str);
  }

  public static resetChecklistState(contentId: string, checklistKey: string) {
    removeItem(LocalStorage.getChecklistStateKey(checklistKey, contentId));
  }

  public static getChecklistState(contentId: string, checklistKey: string): ChecklistState {
    const localStorageValue = readItem(LocalStorage.getChecklistStateKey(checklistKey, contentId));
    let checklist: ChecklistState;
    if (localStorageValue) {
      try {
        checklist = serializer.deserialize<ChecklistState>(localStorageValue, ChecklistState);
      } catch (err) {
        localLog.warn({ err }, 'Error while deserizalizing checklist status.');
        LocalStorage.resetChecklistState(contentId, checklistKey);
      }
    }
    // @ts-expect-error TS2454 - strict migration
    return checklist;
  }

  public static setTableQueryFilter(tableId: string, query: string) {
    writeItem(LocalStorage.getTableQueryFilterKey(tableId), query);
  }

  public static getTableQueryFilter(tableId: string): string | null {
    const query = readItem(LocalStorage.getTableQueryFilterKey(tableId));
    return query;
  }

  public static removeTableQueryFilter(tableId: string) {
    removeItem(LocalStorage.getTableQueryFilterKey(tableId));
  }

  public static getPreLoginRoute() {
    return readItem(LocalStorage.PRE_LOGIN_ROUTE);
  }

  // @ts-expect-error TS7006 - strict migration
  public static setPreLoginRoute(value) {
    writeItem(LocalStorage.PRE_LOGIN_ROUTE, value);
  }

  public static removePreLoginRoute() {
    removeItem(LocalStorage.PRE_LOGIN_ROUTE);
  }

  public static getUpdatedComments(contentId: string) {
    const updatedComments = readItem(LocalStorage.getUpdatedCommentsKey(contentId));
    return updatedComments ? JSON.parse(updatedComments) : null;
  }

  public static setUpdatedComments(
    contentId: string,
    sectionComments: InlineComment[],
    editorId: string,
  ) {
    let existingUpdatedComments = LocalStorage.getUpdatedComments(contentId);
    if (!existingUpdatedComments) {
      existingUpdatedComments = {};
    }
    existingUpdatedComments[editorId] = sectionComments;
    const str = JSON.stringify(existingUpdatedComments);
    writeItem(LocalStorage.getUpdatedCommentsKey(contentId), str);
  }

  public static removeUpdatedComments(contentId: string) {
    const key = LocalStorage.getUpdatedCommentsKey(contentId);
    removeItem(key);
  }

  public static getBannerDismissalTimestamp(bannerId: string): number | null {
    const timestamp = readItem(LocalStorage.getBannerDismissalKey(bannerId));
    return timestamp ? parseInt(timestamp, 10) : null;
  }

  public static setBannerDismissalTimestamp(bannerId: string, timestamp: number) {
    writeItem(LocalStorage.getBannerDismissalKey(bannerId), timestamp.toString());
  }

  public static getNavigationOpen(): boolean {
    const value = readItem(LocalStorage.NAVIGATION_OPEN);
    // Default to true when no value is stored or storage is unavailable; only 'false' explicitly closes it.
    return value !== 'false';
  }

  public static setNavigationOpen(isOpen: boolean) {
    writeItem(LocalStorage.NAVIGATION_OPEN, isOpen ? 'true' : 'false');
  }

  /** Whether commenting mode is enabled in viewing mode. Defaults to false. */
  public static getViewCommentingMode(): boolean {
    const value = readItem(LocalStorage.VIEW_COMMENTING_MODE);
    return value === 'true';
  }

  public static setViewCommentingMode(isEnabled: boolean) {
    writeItem(LocalStorage.VIEW_COMMENTING_MODE, isEnabled ? 'true' : 'false');
  }

  private static STATE_VALUE = 'state';
  private static PRE_LOGIN_ROUTE = 'preLoginRoute';
  private static NAVIGATION_OPEN = 'navigationOpen';
  private static VIEW_COMMENTING_MODE = 'viewCommentingMode';
  private static getTableQueryFilterKey = (tableId: string) => `tableQueryFilter:${tableId}`;
  private static getTableSettingOptionsKey = (tableId: string) => `tableSettingOptions:${tableId}`;
  private static getTabSettingOptionsKey = (tabId: string) => `tabSettingOptions:${tabId}`;
  private static getContentWorkingCopyKey = (workingCopyKey: string, id: string) =>
    `${workingCopyKey}:${id}`;
  private static getCollectionWorkingCopyKey = (workingCopyKey: string, id: string) =>
    `${workingCopyKey}:${id}`;
  private static getGraphSettingKey = (graphId: string) => `graphSettingOptions:${graphId}`;
  private static getExpandableSectionStateKey = (expandableSectionId: string) =>
    `expandableSectionState:${expandableSectionId}`;
  private static getContentChecklistStateKey = (contentId: string) =>
    `contentChecklistState:${contentId}`;
  private static getChecklistStateKey = (checklistKey: string, contentId: string) =>
    `${checklistKey}:${contentId}`;
  private static getContentQualityQuestionnaireStateKey = (contentId: string, workflowId: number) =>
    `questionnaireState:${contentId}:${workflowId}`;
  private static getUpdatedCommentsKey = (contentId: string) => `updatedComments${contentId}`;
  private static getBannerDismissalKey = (bannerId: string) => `bannerDismissal:${bannerId}`;
}

export default LocalStorage;
